import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getWatchedPicks, deleteWatchedPick, clearAllWatchedPicks, resetSimulatedBalance } from '../services/api';
import { formatPickTypeLabel, formatPickValue } from '../utils/formatPickType';
import { buildBalanceHistoryPoints, computeResolvedRecord } from '../utils/trackedPicksStats';
import PlayerWithAvatar from '../components/PlayerWithAvatar';
import TrackedBalanceChart from '../components/TrackedPicks/TrackedBalanceChart';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
`;

const Header = styled.div`
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: ${props => props.theme.primaryText};
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: ${props => props.theme.secondaryText};
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  @media (min-width: 720px) {
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
    align-items: stretch;
  }
`;

const BalanceCard = styled.div`
  background: ${props => props.theme.secondaryBg || 'rgba(255,255,255,0.06)'};
  border: 1px solid ${props => props.theme.border};
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const BalanceTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  min-width: 0;
`;

const BalanceLabel = styled.span`
  color: ${props => props.theme.secondaryText};
  font-size: 0.9rem;
`;

const BalanceAmount = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.primaryText};
  line-height: 1.2;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  min-width: 0;
`;

const StatCard = styled.div`
  background: ${props => props.theme.secondaryBg || 'rgba(255,255,255,0.06)'};
  border: 1px solid ${props => props.theme.border};
  border-radius: 0.5rem;
  padding: 0.85rem 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
`;

const StatLabel = styled.span`
  font-size: 0.8rem;
  color: ${props => props.theme.secondaryText};
  margin-bottom: 0.25rem;
`;

const StatValue = styled.span`
  font-size: 1.35rem;
  font-weight: 700;
  color: ${props => props.theme.primaryText};
`;

const ResetBalanceButton = styled.button`
  padding: 0.4rem 0.75rem;
  font-size: 0.85rem;
  background: ${props => props.theme.inputBg};
  border: 1px solid ${props => props.theme.border};
  border-radius: 0.35rem;
  color: ${props => props.theme.secondaryText};
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

const ClearHistoryButton = styled.button`
  padding: 0.4rem 0.75rem;
  font-size: 0.85rem;
  background: transparent;
  border: 1px solid ${props => props.theme.border};
  border-radius: 0.35rem;
  color: ${props => props.theme.secondaryText};
  cursor: pointer;
  &:hover { color: ${props => props.theme.primaryText}; border-color: ${props => props.theme.secondaryText}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ToolbarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Filters = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  @media (max-width: 600px) {
    gap: 0.35rem;
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 2px;
    -webkit-overflow-scrolling: touch;
  }
`;

const FilterButton = styled.button`
  padding: 0.4rem 0.75rem;
  font-size: 0.875rem;
  background: ${props => props.$active ? props.theme.primary : 'transparent'};
  color: ${props => props.$active ? (props.theme.primaryContrast || '#fff') : props.theme.primaryText};
  border: 1px solid ${props => props.theme.border};
  border-radius: 0.35rem;
  cursor: pointer;
  &:hover { opacity: 0.9; }
  @media (max-width: 600px) {
    padding: 0.35rem 0.5rem;
    font-size: 0.8rem;
    flex-shrink: 0;
  }
`;

const PickCard = styled.div`
  background: ${props => props.theme.cardBg || props.theme.primaryBg};
  border: 1px solid ${props => props.theme.border};
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  margin-bottom: 0.75rem;
`;

const PickRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const PickMain = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  min-width: 0;
`;

const PickMeta = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.secondaryText};
  margin-top: 0.35rem;
`;

const OutcomeBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  flex-shrink: 0;
  &.pending { background: ${props => props.theme.secondaryBg}; color: ${props => props.theme.secondaryText}; }
  &.won { background: rgba(72,187,120,0.25); color: #38a169; }
  &.lost { background: rgba(229,62,62,0.2); color: #e53e3e; }
`;

const RemoveButton = styled.button`
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  background: transparent;
  border: 1px solid ${props => props.theme.border};
  border-radius: 0.25rem;
  color: ${props => props.theme.secondaryText};
  cursor: pointer;
  &:hover { color: ${props => props.theme.primaryText}; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${props => props.theme.secondaryText};
`;

const TrackedPicksPage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | resolved | won | lost
  const [resettingBalance, setResettingBalance] = useState(false);
  const [clearingHistory, setClearingHistory] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    getWatchedPicks({})
      .then(setData)
      .catch(() =>
        setData({
          watched_picks: [],
          total_tracked_picks: 0,
          simulated_balance_cents: null,
          simulated_balance_dollars: null,
        }),
      )
      .finally(() => setLoading(false));
  }, [user]);

  const handleResetBalance = async () => {
    setResettingBalance(true);
    try {
      const res = await resetSimulatedBalance();
      setData((prev) => (prev ? { ...prev, simulated_balance_cents: res.simulated_balance_cents, simulated_balance_dollars: res.simulated_balance_dollars } : prev));
    } finally {
      setResettingBalance(false);
    }
  };

  const handleRemove = async (id) => {
    try {
      await deleteWatchedPick(id);
      setData((prev) => {
        const total = prev?.total_tracked_picks ?? prev?.watched_picks?.length ?? 0;
        return {
          ...prev,
          watched_picks: (prev?.watched_picks || []).filter((p) => p.id !== id),
          total_tracked_picks: Math.max(0, total - 1),
          simulated_balance_cents: prev?.simulated_balance_cents,
          simulated_balance_dollars: prev?.simulated_balance_dollars,
        };
      });
    } catch (err) {
      console.error('Failed to remove pick', err);
    }
  };

  const handleClearBetHistory = async () => {
    const n = data?.total_tracked_picks ?? data?.watched_picks?.length ?? 0;
    if (n === 0) return;
    const ok = window.confirm(
      'Remove all tracked picks from your history? Any pending simulated wagers will be refunded to your balance. This cannot be undone.',
    );
    if (!ok) return;
    setClearingHistory(true);
    try {
      const res = await clearAllWatchedPicks();
      setData((prev) => ({
        watched_picks: [],
        total_tracked_picks: 0,
        simulated_balance_cents: res.simulated_balance_cents ?? prev?.simulated_balance_cents,
        simulated_balance_dollars: res.simulated_balance_dollars ?? prev?.simulated_balance_dollars,
      }));
    } catch (err) {
      console.error('Failed to clear bet history', err);
    } finally {
      setClearingHistory(false);
    }
  };

  if (!user) return null;

  const picks = data?.watched_picks || [];
  const displayedPicks =
    statusFilter === 'won' ? picks.filter((p) => p.outcome === 'won') : statusFilter === 'lost' ? picks.filter((p) => p.outcome === 'lost') : statusFilter === 'pending' ? picks.filter((p) => p.outcome === 'pending') : statusFilter === 'resolved' ? picks.filter((p) => p.outcome !== 'pending') : picks;
  const balanceDollars = data?.simulated_balance_dollars ?? (data?.simulated_balance_cents != null ? (data.simulated_balance_cents / 100).toFixed(2) : null);
  const balanceCents = data?.simulated_balance_cents ?? null;
  const { points: balanceChartPoints, hasWagerHistory } = buildBalanceHistoryPoints(picks, balanceCents);
  const { winPct, recordLabel, resolved } = computeResolvedRecord(picks);

  return (
    <Container>
      <Header>
        <Title theme={theme}>My Tracked Picks</Title>
        <Subtitle theme={theme}>
          Picks you're tracking and their outcomes. Add a simulated wager on Game Picks to see profit/loss here.
        </Subtitle>
      </Header>

      {balanceDollars != null && (
        <SummaryRow>
          <BalanceCard theme={theme}>
            <BalanceTextBlock>
              <BalanceLabel theme={theme}>Simulated balance</BalanceLabel>
              <BalanceAmount theme={theme}>${Number(balanceDollars).toFixed(2)}</BalanceAmount>
            </BalanceTextBlock>
            <ResetBalanceButton theme={theme} onClick={handleResetBalance} disabled={resettingBalance}>
              {resettingBalance ? 'Resetting…' : 'Reset to $100'}
            </ResetBalanceButton>
          </BalanceCard>
          <StatsGrid>
            <StatCard theme={theme}>
              <StatLabel theme={theme}>Win %</StatLabel>
              <StatValue theme={theme}>
                {resolved > 0 && winPct != null ? `${winPct}%` : '—'}
              </StatValue>
            </StatCard>
            <StatCard theme={theme}>
              <StatLabel theme={theme}>Record</StatLabel>
              <StatValue theme={theme}>{recordLabel}</StatValue>
            </StatCard>
          </StatsGrid>
        </SummaryRow>
      )}

      {!loading && balanceCents != null && (
        <TrackedBalanceChart points={balanceChartPoints} hasWagerHistory={hasWagerHistory} />
      )}

      <ToolbarRow>
        <Filters style={{ marginBottom: 0, flex: '1 1 auto' }}>
          <FilterButton theme={theme} $active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>All</FilterButton>
          <FilterButton theme={theme} $active={statusFilter === 'pending'} onClick={() => setStatusFilter('pending')}>Pending</FilterButton>
          <FilterButton theme={theme} $active={statusFilter === 'resolved'} onClick={() => setStatusFilter('resolved')}>Resolved</FilterButton>
          <FilterButton theme={theme} $active={statusFilter === 'won'} onClick={() => setStatusFilter('won')}>Won</FilterButton>
          <FilterButton theme={theme} $active={statusFilter === 'lost'} onClick={() => setStatusFilter('lost')}>Lost</FilterButton>
        </Filters>
        {(data?.total_tracked_picks ?? data?.watched_picks?.length ?? 0) > 0 && (
          <ClearHistoryButton
            theme={theme}
            type="button"
            onClick={handleClearBetHistory}
            disabled={clearingHistory || loading}
          >
            {clearingHistory ? 'Clearing…' : 'Clear bet history'}
          </ClearHistoryButton>
        )}
      </ToolbarRow>

      {loading && <p style={{ color: theme.secondaryText }}>Loading…</p>}
      {!loading && displayedPicks.length === 0 && (
        <EmptyState theme={theme}>
          <p>
            {picks.length === 0
              ? 'No tracked picks yet.'
              : statusFilter === 'won'
                ? 'No winning picks.'
                : statusFilter === 'lost'
                  ? 'No losing picks.'
                  : 'No picks match this filter.'}
          </p>
          {picks.length === 0 && (
            <p style={{ marginTop: '0.5rem', fontSize: '0.95rem' }}>Go to a <a href="/mlb/hit" style={{ color: theme.primary }}>predictions page</a> or <a href="/game-picks" style={{ color: theme.primary }}>Game Picks</a> and click “Track” on a pick to add it here.</p>
          )}
        </EmptyState>
      )}
      {!loading && displayedPicks.length > 0 && displayedPicks.map((p) => (
        <PickCard key={p.id} theme={theme}>
          <PickRow>
            <PickMain>
              <PlayerWithAvatar playerId={p.player_id} playerName={p.player_name} size={28} />
              <div>
                <span style={{ color: theme.primaryText, fontWeight: 600 }}>
                  {formatPickTypeLabel(p.pick_type)}
                  {p.prediction_value != null && ` (${formatPickValue(p.pick_type, p.prediction_value)})`}
                </span>
                {p.odds_display && <span style={{ marginLeft: '0.35rem', color: theme.secondaryText }}>· {p.odds_display}</span>}
                {p.edge_at_watch != null && <span style={{ marginLeft: '0.25rem', color: theme.secondaryText }}>· {(Number(p.edge_at_watch) * 100).toFixed(1)}% edge</span>}
                <PickMeta theme={theme}>
                  {p.game_date}
                  {p.stake_dollars != null && p.stake_dollars > 0 && ` · $${p.stake_dollars} wager`}
                </PickMeta>
              </div>
            </PickMain>
            <OutcomeBadge theme={theme} className={p.outcome}>{p.outcome}</OutcomeBadge>
            {p.outcome === 'pending' && (
              <RemoveButton theme={theme} onClick={() => handleRemove(p.id)}>Remove</RemoveButton>
            )}
          </PickRow>
          {p.outcome !== 'pending' && (p.actual_value != null || p.pick_type === 'moneyline') && (
            <PickMeta theme={theme} style={{ marginTop: '0.5rem' }}>
              Result: {p.pick_type === 'moneyline' ? (p.outcome === 'won' ? 'Won' : 'Lost') : `${p.actual_value} ${p.pick_type === 'total_bases' ? 'TB' : p.pick_type === 'hr' ? 'HR' : 'hits'}`}
              {p.stake_dollars != null && p.stake_dollars > 0 && p.outcome === 'won' && p.profit_dollars != null && (
                <span style={{ color: theme.success || '#38a169', marginLeft: '0.5rem' }}>
                  · Won +${Number(p.profit_dollars).toFixed(2)} profit
                </span>
              )}
            </PickMeta>
          )}
        </PickCard>
      ))}
    </Container>
  );
};

export default TrackedPicksPage;
